import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import routes from "../../../../routes/routes";
import { RootState } from "../../../../redux/store";
import { useAppDispatch } from "../../../../redux/useDispatch";
import { setUseCase } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { setHasExistingSubscription } from "../../../../redux/slice/subscription/subscriptionSlice";
import KeyHandoverPaymentModelStep from "../steps/KeyHandoverPaymentModelStep";
import { useLocationHydration } from "../hooks/useLocationHydration";
import { useSubscriptionHydration } from "../hooks/useSubscriptionHydration";
import { useSubscriptionUrlSync } from "../hooks/useSubscriptionUrlSync";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";
import { Spinner } from "../../../../components/ui/spinner";
import KeyHandoverNavigateNextAction from "../common/util/KeyHandoverNavigateNextAction";
import { KeyHandoverSubscriptionProvider } from "../context/KeyHandoverSubscriptionContext";

/**
 * PaymentModelContent handles location hydration and displays payment model selection UI
 */
function PaymentModelContent() {
    const hydratedRef = React.useRef(false);

    // Hydrate location from URL (requires KeyHandoverProvider context)
    const locationHydrated = useLocationHydration();

    // Hydrate subscription state from URL
    useSubscriptionHydration();

    // Sync subscription state back to URL
    useSubscriptionUrlSync(hydratedRef);

    // Mark as hydrated after first render
    React.useEffect(() => {
        if (!hydratedRef.current) {
            hydratedRef.current = true;
        }
    }, []);

    // Show loading spinner while location is hydrating
    if (!locationHydrated) {
        return (
            <div className="flex justify-center items-center py-12">
                <Spinner />
            </div>
        );
    }

    return (
        <KeyHandoverSubscriptionProvider>
            <KeyHandoverPaymentModelStep />
        </KeyHandoverSubscriptionProvider>
    );
}

/**
 * KeyHandoverPaymentModelPage allows users to select payment model (subscription vs single-booking)
 */
const KeyHandoverPaymentModelPage = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedPaymentModel = useSelector(
        (state: RootState) => state.bookingCreation.keyHandoverSelectedPaymentModel
    );

    // Initialize use case
    useEffect(() => {
        dispatch(setUseCase({ useCase: "keys" }));
    }, [dispatch]);

    // Smart navigation based on payment model selection
    const handleNext = () => {
        const location = window.location;
        if (selectedPaymentModel === "single-booking") {
            // Flow 1: Single booking → Service Selection
            dispatch(setHasExistingSubscription(null));
            navigate({ pathname: routes.keyHandoverServiceSelectionPath, search: location.search });
        } else if (selectedPaymentModel === "subscription-existing") {
            // Flow 2: Existing subscription → Login/Verify
            dispatch(setHasExistingSubscription("existing"));
            navigate({
                pathname: routes.keyHandoverSubscriptionLoginPath,
                search: location.search,
            });
        } else if (selectedPaymentModel === "subscription-new") {
            // Flow 3: New subscription → Plan Selection
            dispatch(setHasExistingSubscription("new"));
            navigate({
                pathname: routes.keyHandoverSubscriptionPlanSelectionPath,
                search: location.search,
            });
        }
    };

    const bottomActions = (
        <KeyHandoverNavigateNextAction
            onNext={handleNext}
            enabled={Boolean(selectedPaymentModel)}
            bounceKey={selectedPaymentModel}
        />
    );

    return (
        <KeyHandoverPageLayout
            headerText={t("book_location.keys.subscription_model.header_text", {
                defaultValue: "Choose your payment model",
            })}
            backTo={routes.keyHandoverLocationSelectionPath}
            bottomActions={bottomActions}
        >
            <KeyHandoverContentWrapper>
                <PaymentModelContent />
            </KeyHandoverContentWrapper>
        </KeyHandoverPageLayout>
    );
};

export default KeyHandoverPaymentModelPage;
